<template>
    <div class="project-tooltip">
        <div class="project-name">
            {{ project?.name }}
            <Popper hover arrow placement="right" content="Copy to clipboard" class="cc-tooltip">
                <button class="copy-btn" type="button" @click="copy(project?.name, 'name')" aria-label="Copy project name">
                    <span class="dx-icon-copy"></span>
                </button>
            </Popper>            
            <span v-if="copied === 'name'" class="copied">Copied</span>
        </div>
        <div class="project-number">
            {{ project?.projectNumber }}
            <Popper hover arrow placement="right" content="Copy to clipboard" class="cc-tooltip">
                <button class="copy-btn" type="button" @click="copy(project?.projectNumber?.toString() ?? '', 'number')" aria-label="Copy project number">
                    <span class="dx-icon-copy"></span>
                </button>
            </Popper>
            <span v-if="copied === 'number'" class="copied">Copied</span>
        </div>
        <div class="project-description" v-if="project?.description">
            {{ project?.description }}
        </div>
    </div>        
</template>

<script lang="ts" setup>
import { defineProps, ref } from 'vue';  
import { ProjectSelect } from 'src/services/entities/Reporting/ProjectSelect';
import Popper from 'vue3-popper';

const props = defineProps<{
  project: ProjectSelect | null;
}>();

const copied = ref<string | null>(null);

function copy(text: string | undefined, key: 'name' | 'number') {
  if (!text) return;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(() => {
      copied.value = key;
      window.setTimeout(() => (copied.value = null), 1500);
    }).catch(() => fallbackCopy(text, key));
  } else {
    fallbackCopy(text, key);
  }
}

function fallbackCopy(text: string, key: 'name' | 'number') {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
    copied.value = key;
    window.setTimeout(() => (copied.value = null), 1500);
  } finally {
    document.body.removeChild(ta);
  }
}
</script>

<style scoped lang="scss">
.project-tooltip {
  padding: 8px 12px;
  max-width: 340px;
  font-family: inherit;
}

.project-name {
  font-size: 1rem; /* normal size */
  font-weight: 600;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 8px;
}

.project-number {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem; /* smaller */
  margin-top: 2px;
}

.copy-btn {
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 1rem;
  color: #c3c3c3;
  border-radius: 4px;
}


.copied {
  font-size: 0.75rem;
  color: #2a7f3e;
  font-style: italic;
  margin-left: 6px;
}

.project-description {
  font-size: 0.75rem; /* smaller */
  font-style: italic;
  margin-top: 6px;
  white-space: normal;
}
.cc-tooltip {
    font-size: 0.85rem;
    font-weight: 990;
}
</style>